/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  CaptureMode,
  WorkflowDTO,
  isNode,
  FlowNode,
  DocumentType,
  Language,
} from '@facephi/sdk-web-react';

import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import workFlowJson from '../assets/json/workflow.json';
import { WidgetIdList, WidgetNameList } from '../states';
import {
  activeLivenessState,
  advancedTrackingState,
  antispoofingState,
  customerEvaluationState,
  livenessMoveState,
  widgetLogState,
} from '../states/atoms';

type Widgets = {
  [key in WidgetNameList]: string | boolean | number | object;
};

type IConfig = {
  logImages: boolean;
  previewCapture: boolean;
  country: string;
  captureMode: CaptureMode;
  licenseKey?: string;
  documentType?: DocumentType;
  retryOnlyCurrentSide: boolean;
  antispoofEnabled?: boolean;
  recordOnboarding?: boolean;
};

type OnboardingDemo = {
  [key: string]: string | boolean | number | object | undefined;
} & {
  cameraHeight?: number;
  cameraWidth?: number;
};

export function useWorkflow(): {
  loading: boolean;
  config: { [key: string]: string | boolean | number | object };
  getWorkflow(config: IConfig): WorkflowDTO;
} {
  const { i18n } = useTranslation();
  const [workflow] = useState<WorkflowDTO>(
    workFlowJson.data.flow.definition as WorkflowDTO
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [config, setConfig] = useState<Widgets>();
  const antispoofing = useRecoilValue(antispoofingState);
  const activeLiveness = useRecoilValue(activeLivenessState);
  const { livenessMoveSteps, livenessMoveTimeout, livenessMoveFailedAttempts } =
    useRecoilValue(livenessMoveState);
  const widgetLog = useRecoilValue(widgetLogState);
  const advancedTracking = useRecoilValue(advancedTrackingState);
  const customerEvaluation = useRecoilValue(customerEvaluationState);
  const language = Object.values(Language).includes(i18n.language as Language)
    ? i18n.language
    : Language.en;

  const getWidgetCofig = (id: string) => {
    const node = workflow.find(
      (item) => isNode(item) && (item as FlowNode).data.widgetId === id
    );
    return (node as FlowNode)?.data?.configuration?.general || {};
  };

  useEffect(() => {
    setConfig({
      [WidgetNameList.selphi]: getWidgetCofig(
        WidgetIdList[WidgetNameList.selphi]
      ),
      [WidgetNameList.selphid]: getWidgetCofig(
        WidgetIdList[WidgetNameList.selphid]
      ) as OnboardingDemo,
    });

    setLoading(false);
  }, [workflow]);

  const getWorkflow = ({
    logImages,
    previewCapture,
    country,
    captureMode,
    documentType,
    retryOnlyCurrentSide,
  }: IConfig) =>
    workflow.map((item) => {
      if (isNode(item)) {
        const node = cloneDeep(item as FlowNode);
        if (node.data.configuration) {
          node.data.configuration.general = {
            ...node.data.configuration?.general,
            language,
          };
          if (node.data.widgetId === WidgetIdList.selphi) {
            node.data.configuration.general = {
              ...node.data.configuration.general,
              logImages,
              antispoofEnabled: antispoofing,
              showLog: widgetLog,
              livenessMoveSteps: activeLiveness ? livenessMoveSteps : 0,
              livenessMoveTimeout: livenessMoveTimeout,
              livenessMoveFailedAttempts: livenessMoveFailedAttempts,
              interactible: false,
              stabilizationStage: true,
              previewCapture,
              advancedTracking: false,
            };
          }
          if (node.data.widgetId === WidgetIdList.selphid) {
            node.data.configuration.general = {
              ...node.data.configuration.general,
              logImages,
              previewCapture,
              // TO DO REPLACE THIS PATCH WHEN WIDGETS TEAM MAKE ADJUSTMENTS
              country: country === 'BR' ? undefined : country,
              captureMode,
              documentType,
              allowUncertain:
                country === '' || country === 'BR' || country === 'ZA',
              retryOnlyCurrentSide,
              cameraWidth: 1920,
              cameraHeight: 1080,
              showLog: widgetLog,
              engineFallback: advancedTracking,
              advancedTracking: false,
              allowUnknownDocuments: customerEvaluation,
              debugMode: false,
              graphicResources: {
                tutorialAnimations: [],
              },
              // TO DO to change the engine used
              // engineFallback: true | false
            };
          }
        }
        return node;
      }
      return item;
    });

  const getSelphiConfig: any = () => config?.[WidgetNameList.selphi] || {};

  const getSelphidConfig: any = () => config?.[WidgetNameList.selphid] || {};

  return {
    loading,
    config: {
      previewCapture: getSelphidConfig().previewCapture,
      country: getSelphidConfig().country,
      logImages: getSelphiConfig().logImages,
      className: '',
      language: '',
      captureMode: getSelphidConfig().captureMode || CaptureMode.Full,
      licenseKey: getSelphidConfig().licenseKey,
      documentType: getSelphidConfig().documentType,
      cameraHeight: getSelphidConfig().cameraHeight,
      cameraWidth: getSelphidConfig().cameraWidth,
    },
    getWorkflow,
  };
}
